/**
 * The shelf record as `backend-content` serves it from `admin/shelves`.
 *
 * These are local declarations only because `src/shared/api/generated/` is
 * still empty; once `pnpm codegen` has run against the content OpenAPI document
 * they are replaced by the generated DTOs and this file keeps only what the
 * surface itself adds.
 *
 * **Two shapes here are assumed rather than quoted**, because the owning
 * PRD-TDD names the routes and the table columns but pins no wire schema for
 * them. Both are isolated to this file and to `mutations.ts`, so codegen
 * corrects them in one place:
 *
 * - `sourceParam` collapses the table's `source_param_kind` +
 *   `source_param_value` pair into one nullable object, and carries an
 *   **array** even for the two single-valued kinds. The array is what makes the
 *   hand-picked set's order explicit — the catalog calls it «an ordered title
 *   set», and that order is the payload rather than a view preference.
 * - `seeAllUrl` is derived server-side, never authored. The catalog says the
 *   link goes «through to the corresponding filtered listing» — corresponding
 *   to the source — so this surface renders its destination and offers no field.
 */

/**
 * The two home compositions. **Code-owned, not configuration**: the owning
 * table carries a `CHECK` on exactly these two values, and the catalog declares
 * two home shelf lists by name. A third composition is a migration, not a
 * settings write — which is why this is a union here while `source` below is a
 * plain string.
 */
export type ShelfSurface = 'PUBLIC_HOME' | 'SUBSCRIBER_HOME';

export const SHELF_SURFACES: readonly ShelfSurface[] = ['PUBLIC_HOME', 'SUBSCRIBER_HOME'];

/**
 * What a scoped source resolves against. Also code-owned — the owning table
 * carries a `CHECK` on these three — so a configured source with no mapping is
 * treated as parameterless here and the server's own `422` stays the authority.
 */
export type ShelfParamKind = 'CATEGORY' | 'TAG' | 'CONTENT_ITEM_SET';

export interface ShelfSourceParam {
  kind: ShelfParamKind;
  /** Ordered. One member for `CATEGORY` and `TAG`, N for `CONTENT_ITEM_SET`. */
  values: string[];
}

export interface Shelf {
  id: string;
  surface: ShelfSurface;
  /** Rank. **A field, not a view preference** — the order is the data. */
  position: number;
  title: string;
  /**
   * Drawn nowhere on this surface. The shape travels with the shelf's declared
   * identity in configuration and no operator surface offers a layout picker —
   * it is read here only to say why a shelf carries no see-all link.
   */
  layout: string;
  /** A configured value, never a compiled-in member. */
  source: string;
  sourceParam: ShelfSourceParam | null;
  seeAllUrl: string | null;
}

/** What the dialog hands back. The surface and the id come from the caller. */
export interface ShelfDraft {
  title: string;
  source: string;
  /** Empty on a parameterless source; the server refuses a parameter on one. */
  paramValues: string[];
}

/**
 * One `admin/shelves` row **as the service actually serves it**.
 *
 * The two assumptions declared at the top of this file did not survive contact
 * with the deployed route: the rows arrive as a bare array of TABLE COLUMNS —
 * `source_param_kind` and `source_param_value` as a separate pair, `see_all_url`
 * in snake case — and never as the composed `sourceParam` object above. The
 * composition is real and worth keeping, so it happens on the reading side in
 * `queries.ts` rather than being wished onto the wire.
 */
export interface WireShelf {
  id: string;
  surface: ShelfSurface;
  position: number;
  title: string;
  layout: string;
  source: string;
  source_param_kind: string | null;
  source_param_value: string | null;
  see_all_url: string | null;
  is_enabled: boolean;
}

/** The envelope's `data` for a list read — pagination meta is part of the contract. */
export interface ShelfListPayload {
  items: Shelf[];
  page_size: number;
  total: number;
  has_next: boolean;
}

/** One node of the genre tree. Any depth; the picker flattens it. */
/**
 * The genre tree, as the taxonomy surface composes it.
 *
 * Re-exported rather than re-declared: this feature reads the tree through
 * `composeCategoryTree`, so a narrower twin here was a second name for the same
 * runtime object — and the picker could not reuse `flattenTree`, which needs the
 * fields the twin dropped.
 */
export type { CategoryNode, CategoryTreePayload } from '@features/taxonomy/_shared/types';

export interface Tag {
  id: string;
  name: string;
}

export interface TagListPayload {
  items: Tag[];
}

/**
 * `GET`/`PUT /admin/tagStrip` — the curated chip strip.
 *
 * `isCurated` is not derivable from `items`: an uncurated strip answers the whole
 * vocabulary, and a strip curated to hold the whole vocabulary is the same list.
 * The console has to say which of the two an operator is looking at, so the
 * service sends the distinction rather than leaving it to be inferred.
 */
export interface TagStripPayload {
  isCurated: boolean;
  items: TagStripEntry[];
}

/** One chip. `position` is present only on a curated row and is the strip's, not the tag's. */
export interface TagStripEntry {
  id: string;
  slug: string;
  name: string;
  position?: number;
}

export interface TitleSummary {
  id: string;
  title: string;
  /** `DRAFT` | `PUBLISHED` | `UNPUBLISHED`, as the admin list sends it. */
  publish_state: string;
}

export interface TitleListPayload {
  items: TitleSummary[];
  page_size: number;
  total: number;
  has_next: boolean;
}

/** One row of a service's `<service>__configurations` table. */
export interface ConfigurationRow {
  key: string;
  value: string;
}

/**
 * `GET /admin/configurations` returns its rows as the envelope's `data`
 * **directly**, not wrapped in an `items` key — which is what the settings
 * surface, the only other reader of this endpoint, already consumes.
 *
 * **This disagrees with `api-and-data-contracts.md` §4.1**, whose
 * `ModelList<Entity>RowFor<Role>` line puts list rows in `data.items`. The
 * endpoint is unpaginated and `backend-content`'s own PRD-TDD does not state a
 * shape either way, so the disagreement is left standing here rather than
 * settled by one surface: whichever way it resolves, the fixture and both
 * readers move together in one MR.
 */
export type ConfigurationPayload = ConfigurationRow[];
